import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { colors } from '../theme';
import SpeedIcon from '@mui/icons-material/Speed';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import StarIcon from '@mui/icons-material/Star';
import EvStationIcon from '@mui/icons-material/EvStation';
import BessSpecTable from './BessSpecTable';

function ProductsSection() {
  const [activeTab, setActiveTab] = useState(0);
  const publicUrl = process.env.PUBLIC_URL || '';
  const getProductImageUrl = (image) => {
    if (!image) return '';
    const normalized = image.startsWith('/') ? image : `/${image}`;
    return publicUrl ? `${publicUrl}${normalized}` : normalized;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  const products = [
    {
      category: 'AC',
      label: 'AC Chargers',
      icon: <SpeedIcon />,
      items: [
        {
          name: 'SolaCharge AC Charger',
          power: '7.4kW / 22kW',
          specs: [
            'Type 2 Connector',
            'IP54 Rating',
            'App & RFID Access',
            'OCPP 1.6J/2.0.1',
            'Native P&EMS + DLB integration',
          ],
          image: 'products/solacharge-ac.png',
          color: colors.green,
          new: true,
          highlight: 'In-house — fully integrated with our all-in-one solution',
        },
        {
          name: '7.4kW AC Charger',
          power: '7.4kW',
          specs: [
            'Type 2 Connector',
            'IP54 Rating',
            'App & RFID Access',
            'Wall/Pole Mount',
            'OCPP 1.6J/2.0.1',
          ],
          image: 'products/artemis.png',
          color: colors.green,
        },
        {
          name: '22kW AC Charger',
          power: '22kW',
          specs: [
            'Type 2 Connector',
            'IP54 Rating',
            'App & RFID Access',
            'Dynamic Load Management',
            'OCPP 1.6J/2.0.1',
          ],
          image: 'products/versicharge.png',
          color: colors.green,
          popular: true,
        },
      ],
    },
    {
      category: 'DC',
      label: 'DC Fast Chargers',
      icon: <SpeedIcon />,
      items: [
        {
          name: '60kW DC Fast Charger',
          power: '60kW',
          specs: [
            'CCS2 & CHAdeMO',
            'IP54 Rating',
            '20-80% in 30-45 min',
            'Dual gun, shared output',
            'OCPP 1.6J/2.0.1',
          ],
          image: 'products/dc03.png',
          color: colors.yellow,
        },
        {
          name: '120kW DC Fast Charger',
          power: '120kW',
          specs: [
            'CCS2 & CHAdeMO',
            'IP54 Rating',
            '20-80% in 15-25 min',
            'Dual gun, dynamic power sharing',
            'OCPP 1.6J/2.0.1',
          ],
          image: 'products/dc02.png',
          color: colors.yellow,
          popular: true,
        },
        {
          name: '300kW DC Fast Charger',
          power: '300kW',
          specs: [
            'CCS2 & CHAdeMO',
            'IP54 Rating',
            '20-80% in 10-20 min',
            'Ultra-fast charging, optimized for high-performance EVs',
            'OCPP 1.6J/2.0.1',
          ],
          image: 'products/sicharge.png',
          color: colors.yellow,
        },
      ],
    },
    {
      category: 'ESS',
      label: 'Energy Storage',
      icon: <BatteryChargingFullIcon />,
      items: [
        {
          name: 'B-ESS Battery Energy Saving System',
          power: '8-12kW / 20kWh',
          specs: [
            'LFP (Lithium Iron Phosphate) battery',
            'Hybrid inverter: 8kW, 10kW or 12kW, 3-phase',
            '20kWh capacity (10-60kWh options)',
            'Smart BMS with Bluetooth monitoring',
            'Fast DC fuses + automatic fire suppression',
            'SCDF-compliant design, Made in Singapore',
          ],
          image: '',
          color: colors.green,
          highlight: 'Pairs with EV Chargers & Solar',
          new: true,
        },
      ],
    },
  ];

  return (
    <Box
      id="products"
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: colors.white,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 800,
              mb: 2,
              color: colors.black,
            }}
          >
            Our{' '}
            <Box component="span" sx={{ color: colors.yellow }}>
              Products
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              maxWidth: '700px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.2rem' },
            }}
          >
            Premium EV charging equipment and energy storage solutions
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTab-root': {
                fontSize: '1.1rem',
                fontWeight: 700,
                textTransform: 'none',
                minWidth: 120,
                '&.Mui-selected': {
                  color: colors.yellow,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: colors.yellow,
                height: 3,
              },
            }}
          >
            {products.map((category, index) => (
              <Tab
                key={index}
                label={category.category}
                icon={category.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>

        {/* Carousel Container */}
        <Box sx={{ position: 'relative', overflow: 'visible' }}>
          {/* Scrollable Products */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 3,
              pt: 3,
            }}
          >
            {products[activeTab].items.map((product, index) => (
              <Card
                key={index}
                sx={{
                  flex: '1 1 320px',
                  minWidth: 260,
                  maxWidth: 380,
                  height: 550,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 4,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  border: product.popular
                    ? `3px solid ${product.color}`
                    : product.new
                    ? `3px solid ${colors.yellow}`
                    : '1px solid #e0e0e0',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 16px 48px ${product.color}30`,
                  },
                }}
              >
                    {/* Popular Badge */}
                    {product.popular && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          bgcolor: colors.yellow,
                          color: colors.black,
                          px: 2,
                          py: 0.5,
                          borderRadius: 2,
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          zIndex: 1,
                          boxShadow: `0 4px 12px ${colors.yellow}60`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <StarIcon sx={{ fontSize: '1rem' }} />
                        POPULAR
                      </Box>
                    )}

                    {/* New Badge */}
                    {product.new && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          bgcolor: colors.yellow,
                          color: colors.black,
                          px: 3,
                          py: 0.5,
                          borderRadius: 2,
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          zIndex: 1,
                          boxShadow: `0 4px 12px ${colors.yellow}60`,
                        }}
                      >
                        NEW
                      </Box>
                    )}

                    {/* Product Image */}
                    <CardMedia
                      sx={{
                        height: 240,
                        bgcolor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        p: 2.5,
                        borderTopLeftRadius: 32,
                        borderTopRightRadius: 32,
                      }}
                    >
                      {product.image ? (
                        <Box
                          component="img"
                          src={getProductImageUrl(product.image)}
                          alt={product.name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#999',
                          }}
                        >
                          {products[activeTab].category === 'ESS' ? (
                            <BatteryChargingFullIcon sx={{ fontSize: 80, color: product.color, mb: 2 }} />
                          ) : (
                            <EvStationIcon sx={{ fontSize: 80, color: product.color, mb: 2 }} />
                          )}
                          <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#999' }}>
                            Image Coming Soon
                          </Typography>
                        </Box>
                      )}
                    </CardMedia>

                    <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* Product Name */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          color: colors.black,
                          fontSize: '1.15rem',
                        }}
                      >
                        {product.name}
                      </Typography>

                      {/* Power Rating */}
                      <Chip
                        label={product.power}
                        sx={{
                          bgcolor: `${product.color}20`,
                          color:
                            product.color === colors.yellow
                              ? colors.black
                              : product.color,
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          mb: 2,
                        }}
                      />

                      {/* Specs */}
                      <Box sx={{ mt: 1.5 }}>
                        {product.specs.map((spec, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 0.75,
                            }}
                          >
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: product.color,
                                mr: 1.5,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#666',
                                fontSize: '0.9rem',
                              }}
                            >
                              {spec}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Highlight */}
                      {product.highlight && (
                        <Box
                          sx={{
                            mt: 3,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: `${colors.yellow}20`,
                            border: `2px solid ${colors.yellow}`,
                          }}
                        >
                          <Typography
                            sx={{
                              color: colors.black,
                              fontWeight: 700,
                              fontSize: '0.9rem',
                              textAlign: 'center',
                            }}
                          >
                            ⚡ {product.highlight}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>

            {/* B-ESS Technical Data - shown on ESS tab */}
            {products[activeTab].category === 'ESS' && <BessSpecTable />}
          </Container>
        </Box>
      );
    }
    
    export default ProductsSection;
